import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FiCheckCircle, FiXCircle, FiInfo, FiMessageSquare, FiDownload } from 'react-icons/fi';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Helper function to format display names (remains unchanged)
const getDirectorApprovalDisplay = (directorApproval) => {
    if (!directorApproval || !directorApproval.status) return 'N/A';
    const status = directorApproval.status;
    const approvedBy = directorApproval.approvedBy ? `${directorApproval.approvedBy.firstName || ''} ${directorApproval.approvedBy.lastName || ''}`.trim() : '';
    const approvedAt = directorApproval.approvedAt ? new Date(directorApproval.approvedAt).toLocaleDateString() : '';
    let display = '';
    switch (status) {
        case 'pending': display = 'Pending Decision'; break;
        case 'approved': display = `Approved by ${approvedBy} on ${approvedAt}`; break;
        case 'rejected': display = `Rejected by ${approvedBy} on ${approvedAt}`; break;
        case 'commented': display = `Reviewed & Forwarded by ${approvedBy} on ${approvedAt}`; break;
        default: display = 'N/A'; break;
    }
    if (directorApproval.comments) {
        display += ` (Comments: ${directorApproval.comments})`;
    }
    return display;
};

// Helper function to get status label (remains unchanged)
const getStatusLabel = (status, REQUISITION_STATUS_FILTER_OPTIONS) => {
    return REQUISITION_STATUS_FILTER_OPTIONS.find(opt => opt.value === status)?.label || status.replace(/_/g, ' ');
};

export default function RequisitionsTable({ 
    requisitions = [], 
    loading, 
    error, 
    handleOpenCommentsModal, 
    handleViewRequisitionDetails, 
    EXECUTIVE_APPROVAL_THRESHOLD,
    REQUISITION_STATUS_FILTER_OPTIONS
}) {
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const requisitionsPerPage = 15;

    const filteredRequisitions = useMemo(() => {
        const q = search.toLowerCase();
        return requisitions.filter((req) =>
            [
                req.title,
                req.typeOfProcurement,
                req.currentStatus,
                req.requisitionBy?.firstName,
                req.requisitionBy?.lastName,
                req.projectId?.projectTitle
            ].filter(Boolean)
            .some((field) => String(field).toLowerCase().includes(q))
        );
    }, [requisitions, search]);

    const totalPages = Math.ceil(filteredRequisitions.length / requisitionsPerPage);

    const currentRequisitions = useMemo(() => {
        const start = (currentPage - 1) * requisitionsPerPage;
        return filteredRequisitions.slice(start, start + requisitionsPerPage);
    }, [filteredRequisitions, currentPage]);

    useEffect(() => setCurrentPage(1), [search]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setExportDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const mapRequisitionToExportRow = (req) => ({
        "Requisition ID": req._id,
        "Title": req.title,
        "Requested By": req.requisitionBy ? `${req.requisitionBy.firstName} ${req.requisitionBy.lastName}` : "N/A",
        "Project": req.projectId ? req.projectId.projectTitle : "N/A",
        "Type": req.typeOfProcurement,
        "Current Status": getStatusLabel(req.currentStatus, REQUISITION_STATUS_FILTER_OPTIONS),
        "Director Approval": getDirectorApprovalDisplay(req.directorApproval),
        "Total Amount (USD)": req.totalAmount || 0,
        "Priority": req.priority,
        "Due Date": new Date(req.dueDate).toLocaleDateString(),
        "Description": req.description || ""
    });

    const exportToExcel = () => {
        if (!filteredRequisitions.length) {
            alert("No data to export!");
            return;
        }
        const exportData = filteredRequisitions.map(mapRequisitionToExportRow);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Requisitions");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(dataBlob, "requisitions_export.xlsx");
    };

    const exportToCSV = () => {
        if (!filteredRequisitions.length) {
            alert("No data to export!");
            return;
        }
        const exportData = filteredRequisitions.map(mapRequisitionToExportRow);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const csvBlob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(csvBlob, "requisitions_export.csv");
    };

    const exportToPDF = () => {
        if (!filteredRequisitions.length) {
            alert("No data to export!");
            return;
        }
        const exportData = filteredRequisitions.map(mapRequisitionToExportRow);
        const doc = new jsPDF("l", "pt", "a4");
        const columns = Object.keys(exportData[0]);
        autoTable(doc, {
            head: [columns],
            body: exportData.map(row => columns.map(col => row[col])),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [237, 125, 49], textColor: [255, 255, 255] },
            theme: "striped",
            margin: { left: 20, right: 20 },
        });
        doc.save("requisitions_export.pdf");
    };

    const handleExport = (format) => {
        setExportDropdownOpen(false);
        switch (format) {
            case "excel": exportToExcel(); break;
            case "csv": exportToCSV(); break;
            case "pdf": exportToPDF(); break;
            default: alert("Unsupported export format");
        }
    };
    
    return (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <input
                    type="search"
                    placeholder="Search requisitions..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full sm:max-w-xs rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    aria-label="Search requisitions"
                />

                <div className="relative inline-block text-left" ref={dropdownRef}>
                    <button
                        onClick={() => setExportDropdownOpen((open) => !open)}
                        type="button"
                        className="inline-flex justify-center w-full rounded bg-orange-600 px-4 py-2 text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        aria-haspopup="true"
                        aria-expanded={exportDropdownOpen}
                        aria-controls="export-menu"
                    >
                        <FiDownload className="mr-2" size={18} />
                        Export
                        <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                        </svg>
                    </button>
                    {exportDropdownOpen && (
                        <div
                            id="export-menu"
                            className="origin-top-right absolute right-0 mt-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                            role="menu"
                            aria-orientation="vertical"
                            tabIndex={-1}
                        >
                            <div className="py-1">
                                {["excel", "csv", "pdf"].map((format) => (
                                    <button
                                        key={format}
                                        onClick={() => handleExport(format)}
                                        className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-orange-100 hover:text-orange-900"
                                        role="menuitem"
                                    >
                                        Export as {format.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            <div className="overflow-x-auto">
                {loading ? (
                    <p className="text-center py-8 text-gray-500">Loading requisitions...</p>
                ) : error ? (
                    <p className="text-center py-8 text-red-500">Error: {error}</p>
                ) : currentRequisitions.length === 0 ? (
                    <div className="text-center py-10">
                        <p className="text-gray-500 mb-4">No requisitions found matching your criteria.</p>
                        <p className="text-gray-500 text-sm">When Program Managers or other Directors submit new requisitions, they will appear here for your review.</p>
                    </div>
                ) : (
                     
                 <table className="min-w-full border border-gray-200 divide-y divide-gray-200 rounded-lg shadow-sm text-sm">
            <thead className="bg-orange-100">
              <tr>
                <th className="p-3 text-left text-orange-600 font-semibold">Title</th>
                <th className="p-3 text-left text-orange-600 font-semibold">Requested By</th>
                <th className="p-3 text-left text-orange-600 font-semibold">Status</th>
                <th className="p-3 text-left text-orange-600 font-semibold">Priority</th>
                <th className="p-3 text-left text-orange-600 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentRequisitions.map((req) => {
                const isOverThreshold = req.totalAmount > EXECUTIVE_APPROVAL_THRESHOLD;
                return (
                  <tr key={req._id} className="hover:bg-gray-50">
                    <td className="p-3 font-medium text-gray-900 break-words">{req.title}</td>
                    <td className="p-3 text-gray-500">{req.requisitionBy ? `${req.requisitionBy.firstName} ${req.requisitionBy.lastName}` : "N/A"}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          req.currentStatus.includes("completed") ? "bg-green-100 text-green-800" :
                          req.currentStatus.includes("approved_by_director") ? "bg-blue-100 text-blue-800" :
                          req.currentStatus.includes("pending_executive_approval") ? "bg-purple-100 text-purple-800" :
                          req.currentStatus.includes("pending") ? "bg-yellow-100 text-yellow-800" :
                          req.currentStatus.includes("rejected") ? "bg-red-100 text-red-800" :
                          "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {getStatusLabel(req.currentStatus, REQUISITION_STATUS_FILTER_OPTIONS)}
                      </span>
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          req.priority === "urgent" ? "bg-red-100 text-red-800" :
                          req.priority === "high" ? "bg-orange-100 text-orange-800" :
                          "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {req.priority}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                        {['submitted', 'pending_director_approval'].includes(req.currentStatus) && !isOverThreshold && (
                          <>
                            <button
                              onClick={() => handleOpenCommentsModal(req, 'approve')}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-emerald-100 transition-all active:scale-95"
                            >
                              <FiCheckCircle size={14} strokeWidth={3}/> Approve
                            </button>
                            <button
                              onClick={() => handleOpenCommentsModal(req, 'reject')}
                              className="bg-rose-500 hover:bg-rose-600 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-rose-100 transition-all active:scale-95"
                            >
                              <FiXCircle size={14} strokeWidth={3}/> Reject
                            </button>
                          </>
                        )}
                        {['submitted', 'pending_director_approval'].includes(req.currentStatus) && isOverThreshold && (
                          <button
                            onClick={() => handleOpenCommentsModal(req, 'approve')}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-blue-100 transition-all active:scale-95"
                          >
                            <FiMessageSquare size={14} strokeWidth={3}/> Forward
                          </button>
                        )}
                        <button
                          onClick={() => handleViewRequisitionDetails(req)}
                          className="text-gray-400 hover:text-orange-600 px-2 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 transition-all"
                        >
                          <FiInfo size={14} strokeWidth={3}/> Details
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>   
                    
                )}
            </div>

            {totalPages > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                    <button
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={`rounded px-3 py-1 ${currentPage === 1
                            ? "cursor-not-allowed bg-gray-300"
                            : "bg-orange-100 text-orange-600 hover:bg-orange-200"
                            }`}
                        aria-label="Previous page"
                    >
                        Prev
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentPage(i + 1)}
                            className={`rounded px-3 py-1 ${currentPage === i + 1
                                ? "bg-orange-300 text-orange-900 font-semibold"
                                : "bg-gray-200 hover:bg-gray-300"
                                }`}
                            aria-label={`Go to page ${i + 1}`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={`rounded px-3 py-1 ${currentPage === totalPages
                            ? "cursor-not-allowed bg-gray-300"
                            : "bg-orange-100 text-orange-600 hover:bg-orange-200"
                            }`}
                        aria-label="Next page"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}